import { Box, Flex, Loader, Center } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { EditorHelpers } from 'src/common/components/markdown-editor/plugins/EditorReadyPlugin';
import { GroupedSuggestions } from 'src/common/components/markdown-editor/plugins/SuggestionTrackerPlugin';
import { useContentMeta } from 'src/UI/app-container';
import { SiestaMode } from 'src/UI/app-container/types';
import { useAppContainer } from 'src/UI/app-container/useAppContainer';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { useCreateDocumentVersion } from '../hooks/useCreateDocumentVersion';
import { useDeleteDocumentVersions } from '../hooks/useDeleteDocumentVersions';
import { useDocumentVersions } from '../hooks/useDocumentVersions';
import { useUpdateDocumentVersion } from '../hooks/useUpdateDocumentVersion';
import { DocumentPanel } from './components';
import SiestaActionBar from './components/SiestaActionBar';
import { formatVersionDate } from './utils/formatVersionDate';

export default function SuitabilityReportEditPage() {
  const { clientId, id } = useParams<{ clientId: string; id: string }>();
  const [suggestions, setSuggestions] = useState<GroupedSuggestions>({
    default: {},
    note: {},
    inline: {},
  });
  const [showMarkers, setShowMarkers] = useState(true);
  const { versions, isLoading: isLoadingVersions, refetch } = useDocumentVersions(id);
  const { createDocumentVersion } = useCreateDocumentVersion(id);
  const { deleteDocumentVersions } = useDeleteDocumentVersions(id);
  const { setPrimaryVersion } = useUpdateDocumentVersion(id);
  const { createLink } = useFileTemporaryLinkMutation();
  const editorHelpersRef = useRef<EditorHelpers | null>(null);
  const previousSiestaModeRef = useRef<SiestaMode | null>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef(suggestions);
  suggestionsRef.current = suggestions;
  const { siestaMode, setSiestaMode, setSiestaBusyGlowTop } = useAppContainer();

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [preInlineEditContent, setPreInlineEditContent] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const inlineSuggestions = useMemo(
    () => Object.values(suggestions.inline || {}),
    [suggestions.inline],
  );

  const primaryVersion = useMemo(() => versions.find(v => v.isPrimary), [versions]);

  const selectedVersion = useMemo(() => {
    if (selectedVersionId) {
      return versions.find(v => v.id === selectedVersionId);
    }
    return primaryVersion;
  }, [versions, selectedVersionId, primaryVersion]);

  useEffect(() => {
    setSelectedVersionId(currentId => {
      const versionExists = currentId && versions.find(v => v.id === currentId);
      if (!versionExists && primaryVersion) {
        return primaryVersion.id;
      }
      return currentId;
    });
  }, [versions, primaryVersion]);

  const isViewingPrimary = selectedVersion?.id === primaryVersion?.id;

  useEffect(() => {
    if (inlineSuggestions.length > 0 && actionBarRef.current) {
      const rect = actionBarRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const percent = (centerY / window.innerHeight) * 100;
      setSiestaBusyGlowTop(percent);
    }
  }, [inlineSuggestions.length, setSiestaBusyGlowTop]);

  useContentMeta({
    pageType: 'suitability-report-edit',
    entityId: id,
  });

  const handleEditorReady = useCallback((helpers: EditorHelpers) => {
    editorHelpersRef.current = helpers;
  }, []);

  const handleSelectVersion = useCallback((versionId: string) => {
    setSelectedVersionId(versionId);
  }, []);

  const handleMakePrimary = useCallback(
    (versionId: string) => {
      setPrimaryVersion(versionId);
      setSelectedVersionId(versionId);
    },
    [setPrimaryVersion],
  );

  const handleSaveVersion = useCallback(async () => {
    try {
      const content = editorHelpersRef.current?.getContent();
      if (!content) return;
      await createDocumentVersion({ markdownContent: content });
      await refetch();
    } catch (error) {
      notifyAndLogError('Failed to save version', 'Please try again.', error, { documentId: id });
    }
  }, [createDocumentVersion, refetch, id]);

  const handleRemoveVersion = useCallback(
    async (versionId: string) => {
      try {
        await deleteDocumentVersions([versionId]);
        await refetch();
      } catch (error) {
        notifyAndLogError('Failed to remove version', 'Please try again.', error, {
          documentId: id,
          versionId,
        });
      }
    },
    [deleteDocumentVersions, refetch, id],
  );

  const handleOverwriteVersion = useCallback(
    async (versionId: string) => {
      try {
        const content = editorHelpersRef.current?.getContent();
        if (!content) return;
        await deleteDocumentVersions([versionId]);
        await createDocumentVersion({ markdownContent: content });
        await refetch();
      } catch (error) {
        notifyAndLogError('Failed to overwrite version', 'Please try again.', error, {
          documentId: id,
          versionId,
        });
      }
    },
    [deleteDocumentVersions, createDocumentVersion, refetch, id],
  );

  const handleSave = useCallback(async () => {
    if (!selectedVersion) return;
    setIsSaving(true);
    try {
      const content = editorHelpersRef.current?.getContent();
      if (!content) return;
      const oldVersionId = selectedVersion.id;
      const result = await createDocumentVersion({ markdownContent: content });
      const newVersionId = result.data.id;
      await deleteDocumentVersions([oldVersionId]);
      setSelectedVersionId(newVersionId);
      await refetch();
    } catch (error) {
      notifyAndLogError('Failed to save version', 'Please try again.', error, {
        documentId: id,
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedVersion, deleteDocumentVersions, createDocumentVersion, refetch, id]);

  const handleFinalise = useCallback(async () => {
    alert('TODO');
  }, []);

  const handleDownloadPdf = useCallback(
    async (fileId: number) => {
      try {
        const link = await createLink(fileId);
        window.open(link.url, '_blank');
      } catch (error) {
        notifyAndLogError(
          'Failed to download PDF',
          'An error occurred while trying to download the PDF. Please try again later.',
          error,
          { documentId: id, fileId },
        );
      }
    },
    [createLink, id],
  );

  const handleInlineEditStart = useCallback(
    (top: number) => {
      const content = editorHelpersRef.current?.getContent();
      if (content) {
        setPreInlineEditContent(content);
      }
      setSiestaBusyGlowTop(top);
      previousSiestaModeRef.current = siestaMode === 'busy' ? 'action' : siestaMode;
      setSiestaMode('busy');
    },
    [siestaMode, setSiestaMode, setSiestaBusyGlowTop],
  );

  const handleApplyInlineChanges = useCallback(() => {
    editorHelpersRef.current?.applyInlineSuggestions();
    setPreInlineEditContent(null);
    setSiestaBusyGlowTop(null);
    setSiestaMode(previousSiestaModeRef.current ?? 'action');
  }, [setSiestaMode, setSiestaBusyGlowTop]);

  const handleDiscardInlineChanges = useCallback(() => {
    if (preInlineEditContent) {
      editorHelpersRef.current?.setContent(preInlineEditContent);
    }
    setPreInlineEditContent(null);
    setSiestaBusyGlowTop(null);
    setSiestaMode(previousSiestaModeRef.current ?? 'action');
  }, [preInlineEditContent, setSiestaMode, setSiestaBusyGlowTop]);

  const handleInlineEditComplete = useCallback(() => {
    requestAnimationFrame(() => {
      const currentInlineSuggestions = Object.values(suggestionsRef.current.inline || {});
      if (currentInlineSuggestions.length === 0 && preInlineEditContent !== null) {
        setPreInlineEditContent(null);
        setSiestaBusyGlowTop(null);
        setSiestaMode(previousSiestaModeRef.current ?? 'action');
      }
    });
  }, [preInlineEditContent, setSiestaMode, setSiestaBusyGlowTop]);

  if (isLoadingVersions) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  return (
    <Flex direction="column" h="100%" pos="relative">
      <DocumentPanel
        backHref={`/client/${clientId}/document/suitability-reports`}
        title="Suitability Report"
        version={selectedVersion ? `V${selectedVersion.versionNumber}` : ''}
        versionDate={selectedVersion ? formatVersionDate(selectedVersion.createdAt) : ''}
        versionVariant={isViewingPrimary ? 'primary' : 'past'}
        versions={versions}
        selectedVersionId={selectedVersion?.id}
        suggestions={suggestions}
        showMarkers={showMarkers}
        isSaving={isSaving}
        onShowMarkersChange={setShowMarkers}
        onSave={handleSave}
        onSaveVersion={handleSaveVersion}
        onRemoveVersion={handleRemoveVersion}
        onOverwriteVersion={handleOverwriteVersion}
        onSelectVersion={handleSelectVersion}
        onMakePrimary={handleMakePrimary}
        onDownloadPdf={handleDownloadPdf}
        onFinalise={handleFinalise}
      />

      <Box mt="md" style={{ flex: 1, overflow: 'auto' }}>
        {selectedVersion?.markdownContent && (
          <MarkdownEditor
            key={selectedVersion.id}
            defaultValue={selectedVersion.markdownContent}
            bare
            onSuggestionsChange={setSuggestions}
            onEditorReady={handleEditorReady}
            showMarkers={showMarkers}
            enableInlineEditing
            documentId={id}
            clientId={clientId}
            onInlineEditStart={handleInlineEditStart}
            onInlineEditComplete={handleInlineEditComplete}
            hasInlineEdits={inlineSuggestions.length > 0}
          />
        )}
      </Box>

      {inlineSuggestions.length > 0 && (
        <Box ref={actionBarRef} pos="absolute" bottom={40} left={32} right={32}>
          <SiestaActionBar
            suggestions={inlineSuggestions}
            onApply={handleApplyInlineChanges}
            onDiscard={handleDiscardInlineChanges}
          />
        </Box>
      )}
    </Flex>
  );
}
